import { useState } from 'react';

import { TooltipWrapper } from '@/components/ui/TooltipWrapper';
import { DialogBuilder, DialogDescription, DialogTitle } from '@/features/dialog/DialogBuilder';
import { cn } from '@/utils/cn';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import { Button } from './design-system/Button';

interface VersionInfoProps {
  isCollapsed: boolean;
}

const releaseNotes = [
  'Enhanced support for Wayland Devices.',
  'Optimized multi-rail rate shopping.',
  'Streamlined customs process.',
  'Expanded API for third-party integrations.',
  'Improved address accuracy.',
  'Added support for blockchain transactions.',
  'Enhanced reporting dashboards.',
  'Fixed minor bugs and stability improvements.'
];

const currentVersion = '1.1.2';

export default function VersionInfo({ isCollapsed }: VersionInfoProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  return (
    <>
      <DialogBuilder
        open={isOpen}
        onOpenChange={setIsOpen}
        size="md"
        header={() => (
          <>
            <DialogTitle className="text-xl">{t('version.title', { version: currentVersion })}</DialogTitle>
            <DialogDescription className="sr-only">
              {t('version.srDescription', { version: currentVersion })}
            </DialogDescription>
          </>
        )}
        content={() => (
          <div className="max-h-[400px] overflow-y-auto">
            <ul className="text-typography-900 space-y-2 text-sm">
              {releaseNotes.map((note) => (
                <li key={note} className="flex items-start gap-2">
                  <span className="bg-secondary-900 mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full" />
                  <span>{note}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      />
      <div className="bg-secondary-600 border-border-300 sticky bottom-0 flex h-[45px] items-center justify-center border-t">
        <TooltipWrapper
          delayDuration={200}
          trigger={
            <Button
              variant="text"
              size="condensed"
              onClick={() => {
                setIsOpen(true);
              }}
              className={cn(
                'text-typography-400 flex h-full w-full items-center justify-center gap-2 bg-transparent text-xs font-semibold transition-colors hover:bg-transparent hover:text-white focus:outline-none focus-visible:ring-0 active:bg-transparent hover:[&_svg]:text-white',
                isCollapsed ? 'px-1' : 'px-4'
              )}
            >
              <span>
                {isCollapsed
                  ? t('version.buttonShort', { version: currentVersion })
                  : t('version.buttonLong', { version: currentVersion })}
              </span>
              {!isCollapsed && <InfoCircleOutlined className="[&_svg]:text-nav-muted [&_svg]:text-lg" />}
            </Button>
          }
          content={<p className="max-w-xs text-sm">{t('version.viewHistory')}</p>}
        />
      </div>
    </>
  );
}
